import React, {
  useCallback,
  useRef,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { Button, Tooltip } from 'antd';
import {
  CloseOutlined,
  DownloadOutlined,
  FullscreenOutlined,
  RotateLeftOutlined,
  LeftOutlined,
  RightOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import type { ImageItem } from '../types';
import { sortImagesForCompareAscending } from '../compareOrder';

function getCompareGrid(count: number): { cols: number; rows: number } {
  if (count <= 1) return { cols: 1, rows: 1 };
  if (count === 2) return { cols: 2, rows: 1 };
  if (count === 3) return { cols: 3, rows: 1 };
  if (count === 4) return { cols: 2, rows: 2 };
  if (count === 5) return { cols: 3, rows: 2 };
  return { cols: 3, rows: 2 };
}

interface ImageViewerModalProps {
  visible: boolean;
  onCancel: () => void;
  images: ImageItem[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onDelete?: (imageId: number) => void;
  compareMode?: boolean;
  selectedForComparison?: ImageItem[];
}

export const ImageViewerModal: React.FC<ImageViewerModalProps> = ({
  visible,
  onCancel,
  images,
  currentIndex,
  onIndexChange,
  onDelete,
  compareMode = false,
  selectedForComparison = [],
}) => {
  const transformRef = useRef<any>(null);
  const currentImage = images[currentIndex];
  const [isAnimating, setIsAnimating] = useState(false);

  const orderedForComparison = useMemo(
    () => sortImagesForCompareAscending(selectedForComparison),
    [selectedForComparison]
  );

  const handleClose = useCallback(() => {
    setIsAnimating(true);
    setTimeout(() => {
      onCancel();
      setIsAnimating(false);
    }, 200);
  }, [onCancel]);

  const handleDownload = useCallback(() => {
    if (currentImage) {
      const link = document.createElement('a');
      link.href = currentImage.url;
      link.download = currentImage.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [currentImage]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const handleDragStart = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      onIndexChange(currentIndex + 1);
    }
  }, [currentIndex, images.length, onIndexChange]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      onIndexChange(currentIndex - 1);
    }
  }, [currentIndex, onIndexChange]);

  const handleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  }, []);

  const resetZoom = useCallback(() => {
    if (transformRef.current) {
      transformRef.current.resetTransform();
    }
  }, []);

  const handleDelete = useCallback(() => {
    if (currentImage && onDelete) {
      onDelete(currentImage.id);
      setIsAnimating(true);
      setTimeout(() => {
        onCancel();
        setIsAnimating(false);
      }, 200);
    }
  }, [currentImage, onDelete, onCancel]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [visible, handleClose]);

  useEffect(() => {
    if (compareMode) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        handlePrev();
      } else if (event.key === 'ArrowRight') {
        handleNext();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, compareMode, handlePrev, handleNext]);

  if (!visible || !currentImage || images.length === 0) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 z-[99999] flex items-center justify-center bg-black/95 backdrop-blur-sm transition-all duration-300 ease-in-out ${
        visible && !isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ display: visible ? 'flex' : 'none' }}
    >
      <div
        className={`relative flex h-full w-full flex-col bg-black transition-all duration-300 ease-in-out transform ${
          visible && !isAnimating ? 'scale-100' : 'scale-95'
        }`}
      >
        <div
          className={`flex items-center justify-between bg-gradient-to-r from-black/80 to-black/60 backdrop-blur-md border-b border-white/10 px-3 sm:px-6 py-2 sm:py-4 text-white transition-all duration-500 ease-out transform ${
            visible && !isAnimating
              ? 'translate-y-0 opacity-100'
              : 'translate-y-4 opacity-0'
          }`}
        >
          <div className="flex items-center space-x-2 sm:space-x-6 flex-1 min-w-0">
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={handleClose}
              className="text-white hover:bg-white/20 transition-all duration-200 rounded-lg [&:hover_.anticon]:text-white [&_.anticon]:text-white flex-shrink-0"
              size="small"
              title="Back to Gallery"
            />
            <h3 className="text-lg sm:text-xl font-semibold text-white truncate">
              {compareMode ? 'Compare' : 'Viewer'}
            </h3>
            {!compareMode && (
              <div className="hidden sm:flex items-center space-x-2">
                <span className="text-sm text-gray-300 bg-white/10 px-3 py-1 rounded-full">
                  {currentIndex + 1} of {images.length}
                </span>
                <span className="text-sm text-gray-400 truncate">
                  {currentImage?.name}
                </span>
              </div>
            )}
            {!compareMode && (
              <div className="sm:hidden flex items-center space-x-1">
                <span className="text-xs text-gray-300 bg-white/10 px-2 py-1 rounded-full">
                  {currentIndex + 1}/{images.length}
                </span>
                <span className="text-xs text-gray-400 truncate max-w-20">
                  {currentImage?.name}
                </span>
              </div>
            )}
            {compareMode && selectedForComparison.length > 0 && (
              <div className="hidden sm:flex items-center space-x-2 text-sm">
                <span className="text-gray-300 bg-white/10 px-3 py-1 rounded-full">
                  Comparing {selectedForComparison.length} image
                  {selectedForComparison.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}
            {compareMode && selectedForComparison.length > 0 && (
              <div className="sm:hidden flex items-center space-x-1 text-xs">
                <span className="text-gray-300 bg-white/10 px-2 py-1 rounded-full">
                  {selectedForComparison.length} img
                  {selectedForComparison.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-1 sm:space-x-3">
            <Tooltip title="Reset Zoom">
              <Button
                type="text"
                icon={<RotateLeftOutlined />}
                onClick={resetZoom}
                className="text-white hover:bg-white/20 transition-all duration-200 rounded-lg [&:hover_.anticon]:text-white [&_.anticon]:text-white"
                size="small"
              />
            </Tooltip>
            <Tooltip title="Fullscreen">
              <Button
                type="text"
                icon={<FullscreenOutlined />}
                onClick={handleFullscreen}
                className="text-white hover:bg-white/20 transition-all duration-200 rounded-lg [&:hover_.anticon]:text-white [&_.anticon]:text-white"
                size="small"
              />
            </Tooltip>
            <Tooltip title="Download">
              <Button
                type="text"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                className="text-white hover:bg-white/20 transition-all duration-200 rounded-lg [&:hover_.anticon]:text-white [&_.anticon]:text-white"
                size="small"
              />
            </Tooltip>
            {onDelete && (
              <Tooltip title="Delete Image">
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={handleDelete}
                  className="text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-all duration-200 rounded-lg [&:hover_.anticon]:text-red-300 [&_.anticon]:text-red-400"
                  size="small"
                />
              </Tooltip>
            )}
            <Tooltip title="Close (ESC)">
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleClose}
                className="text-white hover:bg-white/20 transition-all duration-200 rounded-lg [&:hover_.anticon]:text-white [&_.anticon]:text-white"
                size="large"
              />
            </Tooltip>
          </div>
        </div>

        <div
          className={`flex-1 relative overflow-auto transition-all duration-700 ease-out transform ${
            visible && !isAnimating
              ? 'scale-100 opacity-100'
              : 'scale-105 opacity-0'
          }`}
        >
          {compareMode && orderedForComparison.length > 0 ? (
            (() => {
              const n = orderedForComparison.length;
              const { cols, rows } = getCompareGrid(n);
              return (
                <div
                  className="h-full w-full p-2 box-border flex items-center justify-center"
                  style={{ minHeight: 0 }}
                >
                  <div
                    className="h-full w-full gap-2 rounded-lg overflow-hidden"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${cols}, 1fr)`,
                      gridTemplateRows: `repeat(${rows}, 1fr)`,
                      maxWidth: '100%',
                      maxHeight: '100%',
                    }}
                  >
                    {orderedForComparison.map((image, index) => (
                      <div
                        key={image.id}
                        className="flex items-center justify-center min-h-0 min-w-0 p-1 bg-black/20 rounded border border-white/10 overflow-hidden"
                      >
                        <TransformWrapper
                          minScale={0.5}
                          maxScale={5}
                          initialScale={1}
                          centerOnInit={true}
                          limitToBounds={true}
                          wheel={{ step: 0.1 }}
                        >
                          <TransformComponent
                            wrapperClass="!w-full !h-full cursor-grab"
                            contentClass="!w-full !h-full !flex !items-center !justify-center"
                          >
                            <div className="relative w-full h-full flex items-center justify-center">
                              <div className="absolute top-0 left-0 bg-black/60 text-white text-xs px-2 py-1 rounded z-10">
                                {index + 1}
                              </div>
                              <img
                                src={image.url}
                                alt={image.name}
                                className="max-h-full max-w-full w-auto h-auto object-contain rounded select-none"
                                style={{
                                  boxShadow:
                                    '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
                                }}
                                onContextMenu={handleContextMenu}
                                onDragStart={handleDragStart}
                              />
                              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-2 py-1 rounded truncate z-10">
                                {image.name}
                              </div>
                            </div>
                          </TransformComponent>
                        </TransformWrapper>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()
          ) : (
            <TransformWrapper
              ref={transformRef}
              minScale={0.5}
              maxScale={5}
              initialScale={1}
              centerOnInit={true}
              limitToBounds={false}
              wheel={{ step: 0.1 }}
              onInit={() => {}}
            >
              <TransformComponent wrapperClass="!w-full !h-full cursor-grab">
                <div className="flex h-full w-full items-center justify-center">
                  <img
                    src={currentImage.url}
                    alt={currentImage.name}
                    className="max-h-full max-w-full object-contain shadow-2xl rounded-lg"
                    style={{
                      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
                      filter: 'drop-shadow(0 10px 8px rgb(0 0 0 / 0.04))',
                    }}
                    onContextMenu={handleContextMenu}
                    onDragStart={handleDragStart}
                    onLoad={() => {
                      setTimeout(() => {
                        if (transformRef.current) {
                          transformRef.current.centerView();
                        }
                      }, 100);
                    }}
                  />
                </div>
              </TransformComponent>
            </TransformWrapper>
          )}
        </div>

        {!compareMode && images.length > 1 && (
          <>
            <Tooltip title="Previous Image (←)">
              <Button
                type="text"
                icon={<LeftOutlined />}
                onClick={handlePrev}
                disabled={currentIndex === 0}
                className={`absolute left-4 top-1/2 -translate-y-1/2 transform rounded-full bg-black/60 backdrop-blur-md text-white hover:bg-white/20 hover:scale-110 disabled:opacity-30 disabled:hover:scale-100 transition-all duration-300 border border-white/10 [&:hover_.anticon]:text-white [&_.anticon]:text-white ${
                  visible && !isAnimating
                    ? 'translate-x-0 opacity-100'
                    : '-translate-x-4 opacity-0'
                }`}
                size="large"
                style={{ width: '48px', height: '48px' }}
              />
            </Tooltip>
            <Tooltip title="Next Image (→)">
              <Button
                type="text"
                icon={<RightOutlined />}
                onClick={handleNext}
                disabled={currentIndex === images.length - 1}
                className={`absolute right-4 top-1/2 -translate-y-1/2 transform rounded-full bg-black/60 backdrop-blur-md text-white hover:bg-white/20 hover:scale-110 disabled:opacity-30 disabled:hover:scale-100 transition-all duration-300 border border-white/10 [&:hover_.anticon]:text-white [&_.anticon]:text-white ${
                  visible && !isAnimating
                    ? 'translate-x-0 opacity-100'
                    : 'translate-x-4 opacity-0'
                }`}
                size="large"
                style={{ width: '48px', height: '48px' }}
              />
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
};
